import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { STAGES } from '@joker/domain'
import type { BucketSummary } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Loading, ErrorNote, ViewTitle } from '../ui'
import { CohortEditor, emptyDraft } from './CohortEditor'

/**
 * Every cohort: a defined group of people. What you do with one — see who is
 * in it, export it, watch it train, follow it through the funnel, send it to
 * Klaviyo — lives on its own page. Buckets were the same thing under another
 * name; they are cohorts now.
 */
export function CohortList({ onOpen, onCompare }: { onOpen: (id: string) => void; onCompare: () => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.buckets.list.queryOptions())
  const [creating, setCreating] = useState(false)

  if (q.error) return <ErrorNote error={q.error} />

  const main = q.data?.filter((b) => !b.system) ?? []
  const system = q.data?.filter((b) => b.system) ?? []

  return (
    <>
      <ViewTitle suit="heart">Cohorts</ViewTitle>
      <p className="view-sub">
        A cohort is a group of people defined by what is true of them: what they bought, where they
        signed up, how far they have trained. Open one to see who is in it, export it, compare its
        training with everybody else's, and later follow it through the funnel or send it to Klaviyo.
      </p>

      {creating ? (
        <CohortEditor
          editing={null}
          initial={emptyDraft()}
          onDone={(id) => {
            setCreating(false)
            if (id) onOpen(id)
          }}
        />
      ) : (
        <div className="cohort-new">
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            New cohort
          </button>
          <button type="button" className="btn ghost" onClick={onCompare}>
            Compare by channel, month, age…
          </button>
        </div>
      )}

      {!q.data ? (
        <Loading what="cohorts" />
      ) : (
        <>
          <CardGrid list={main} onOpen={onOpen} />
          {system.length > 0 && (
            <>
              <h3 className="cohort-section">System</h3>
              <p className="footnote">
                Housekeeping: the only way to reach accounts hidden from every other screen — test
                accounts, spam, replaced enrolments, $1 test orders. They can be edited but not archived.
              </p>
              <CardGrid list={system} onOpen={onOpen} />
            </>
          )}
        </>
      )}
    </>
  )
}

function CardGrid({ list, onOpen }: { list: BucketSummary[]; onOpen: (id: string) => void }) {
  return (
    <div className="bgrid">
      {list.map((b) => (
        <button key={b.id} type="button" className="bcard bcard-link" onClick={() => onOpen(b.id)}>
          <div className="top">
            <h4>{b.name}</h4>
            <span className="cnt num">{b.memberCount}</span>
          </div>
          <div className="rule">{b.description ?? b.ruleDescription}</div>
          <div className="goal">
            {b.audience === 'prospects' ? 'Leads' : 'Patients'}
            {(b.goalStage || b.goalNote) && (
              <>
                {' · goal → '}
                <b>{b.goalStage ? STAGES.find((s) => s.id === b.goalStage)?.label : b.goalNote}</b>
              </>
            )}
          </div>
        </button>
      ))}
    </div>
  )
}
